import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import React, { createContext, useContext, useRef } from "react";

// Alle Graph-Berechtigungen, die diese App braucht.
// Müssen in der App-Registrierung (Entra ID → API-Berechtigungen) eingetragen und konsentiert sein.
const GRAPH_SCOPES = ["https://graph.microsoft.com/.default"];

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// Upload in Blöcken: muss ein Vielfaches von 320 KiB sein
const UPLOAD_CHUNK_SIZE = 320 * 1024 * 16; // 5 MiB

// Ordnerpfade sicher in die URL einbauen (Leerzeichen, #, ?, Umlaute …)
const encodePath = (path) =>
   path
      .split("/")
      .filter(Boolean)
      .map(encodeURIComponent)
      .join("/");

const M365Context = createContext();

export const M365Provider = ({ children }) => {
   const { instance } = useMsal();

   // Verhindert mehrere gleichzeitige Popups (sonst "interaction_in_progress")
   const interactionPromise = useRef(null);

   // ---------------------------------------------------------------------------
   // Token-Handling
   // ---------------------------------------------------------------------------

   function acquireTokenInteractive(request) {
      if (!interactionPromise.current) {
         interactionPromise.current = instance
            .acquireTokenPopup(request)
            .then((res) => {
               instance.setActiveAccount(res.account);
               return res.accessToken;
            })
            .catch(async (err) => {
               // Popup vom Browser blockiert → auf Redirect ausweichen
               if (
                  err instanceof BrowserAuthError &&
                  ["popup_window_error", "empty_window_error"].includes(err.errorCode)
               ) {
                  await instance.acquireTokenRedirect(request);
                  throw new Error("Weiterleitung zur Microsoft-Anmeldung …");
               }
               throw err;
            })
            .finally(() => {
               interactionPromise.current = null;
            });
      }
      return interactionPromise.current;
   }

   async function getAccessToken(scopes = GRAPH_SCOPES) {
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

      if (!account) {
         return acquireTokenInteractive({ scopes });
      }

      const request = { scopes, account };

      try {
         const res = await instance.acquireTokenSilent(request);
         return res.accessToken;
      } catch (err) {
         // Hier landet AADSTS160021 & Co.
         if (err instanceof InteractionRequiredAuthError) {
            return acquireTokenInteractive(request);
         }
         throw err;
      }
   }

   // ---------------------------------------------------------------------------
   // Graph-Helper
   // ---------------------------------------------------------------------------

   async function graphRequest(endpoint, { method = "GET", body, responseType = "json" } = {}) {
      const token = await getAccessToken();

      const response = await fetch(endpoint, {
         method,
         headers: {
            Authorization: `Bearer ${token}`,
            ...(body !== undefined && { "Content-Type": "application/json" }),
         },
         body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
         const details = await response.text();
         throw new Error(`Graph ${method} ${endpoint} → ${response.status}: ${details}`);
      }

      if (responseType === "blob") return response.blob();
      if (response.status === 204) return null;
      return response.json();
   }

   // ---------------------------------------------------------------------------
   // Öffentliche Funktionen
   // ---------------------------------------------------------------------------

   async function getSharepointFile(itemId, driveId) {
      if (!itemId || !driveId) return undefined;
      return graphRequest(`${GRAPH_BASE}/drives/${driveId}/items/${itemId}/content`, { responseType: "blob" });
   }

   async function getMS365User(mailName) {
      const upn = encodeURIComponent(`${mailName}@baurimmo.onmicrosoft.com`);
      return graphRequest(`${GRAPH_BASE}/users/${upn}`);
   }

   async function uploadFileToSharepoint(editedBlob, driveId, folderId, fileName) {
      // 1. Upload-Session anlegen
      const session = await graphRequest(
         `${GRAPH_BASE}/drives/${driveId}/items/${folderId}:/${encodeURIComponent(fileName)}:/createUploadSession`,
         {
            method: "POST",
            body: { item: { "@microsoft.graph.conflictBehavior": "rename" } },
         },
      );

      // 2. Datei in Blöcken hochladen (die uploadUrl enthält bereits die Berechtigung –
      //    hier KEINEN Authorization-Header mitschicken)
      const fileSize = editedBlob.size;
      let start = 0;
      let result = null;

      while (start < fileSize) {
         const end = Math.min(start + UPLOAD_CHUNK_SIZE, fileSize);
         const response = await fetch(session.uploadUrl, {
            method: "PUT",
            headers: { "Content-Range": `bytes ${start}-${end - 1}/${fileSize}` },
            body: editedBlob.slice(start, end),
         });

         if (!response.ok) {
            throw new Error(`Upload fehlgeschlagen (${response.status}): ${await response.text()}`);
         }

         result = await response.json();
         start = end;
      }

      console.log("Datei erfolgreich erstellt:", result?.name);
      return result;
   }
/* 
   async function getSharepointData(myFolderName, myDriveId) {
      if (!myFolderName) return undefined;

      const folderPath = encodePath(myFolderName);
      const childrenUrl = (path) => `${GRAPH_BASE}/drives/${myDriveId}/root:/${path}:/children?$expand=thumbnails`;

      const mainData = await graphRequest(childrenUrl(folderPath));
      const mainItems = mainData.value ?? [];

      const objectTree = {
         [myDriveId]: Object.fromEntries(mainItems.map((item) => [item.id, { ...item }])),
      };

      // Unterordner parallel laden
      const subResults = await Promise.all(
         mainItems.map(async (item) => {
            if (!item.folder) return [];
            const subData = await graphRequest(childrenUrl(`${folderPath}/${encodeURIComponent(item.name)}`));
            const children = subData.value ?? [];
            objectTree[myDriveId][item.id].children = Object.fromEntries(children.map((c) => [c.id, c]));
            return children.map((c) => ({ ...c, parentFolder: item.name }));
         }),
      );

      // Reihenfolge wie vorher: Ordner, danach direkt seine Kinder
      const flatArray = mainItems.flatMap((item, i) => [item, ...subResults[i]]);

      return { flatArray, objectTree };
   } */

      async function getSharepointData(myFolderName, myDriveId, maxDepth = 2) {
   if (!myFolderName) return undefined;

   const folderPath = encodePath(myFolderName);
   const rootUrl = `${GRAPH_BASE}/drives/${myDriveId}/root:/${folderPath}:/children?$expand=thumbnails`;
   // Für Unterordner über die Item-ID gehen, dann muss man keine Pfade mehr zusammenbauen/encoden
   const itemUrl = (id) => `${GRAPH_BASE}/drives/${myDriveId}/items/${id}/children?$expand=thumbnails`;

   // Lädt eine Ebene und (falls erlaubt) rekursiv deren Unterordner
   async function loadLevel(url, depth, parentName) {
      const data = await graphRequest(url);
      const items = data.value ?? [];

      const results = await Promise.all(
         items.map(async (item) => {
            const flatItem = parentName ? { ...item, parentFolder: parentName } : { ...item };
            const treeNode = { ...item };

            if (item.folder && depth < maxDepth) {
               const sub = await loadLevel(itemUrl(item.id), depth + 1, item.name);
               treeNode.children = sub.tree;
               return { flat: [flatItem, ...sub.flat], id: item.id, treeNode };
            }
            return { flat: [flatItem], id: item.id, treeNode };
         }),
      );

      return {
         // Reihenfolge: Ordner, direkt danach seine Kinder (und deren Kinder)
         flat: results.flatMap((r) => r.flat),
         tree: Object.fromEntries(results.map((r) => [r.id, r.treeNode])),
      };
   }

   const { flat, tree } = await loadLevel(rootUrl, 0, null);

   

   return { flatArray: flat, objectTree: { [myDriveId]: tree } };
}

   async function postMsTeamsTeamMessage(teamId, channelId, messagedata) {
      if (!teamId || !channelId) return undefined;
      return graphRequest(
         `${GRAPH_BASE}/teams/${teamId}/channels/${encodeURIComponent(channelId)}/messages`,
         { method: "POST", body: messagedata },
      );
   }

   return (
      <M365Context.Provider
         value={{
            postMsTeamsTeamMessage,
            getMS365User,
            getSharepointData,
            getSharepointFile,
            uploadFileToSharepoint,
            getAccessToken,
         }}
      >
         {children}
      </M365Context.Provider>
   );
};

export const useM365Context = () => useContext(M365Context);